import React, { useState, useMemo } from "react";
import Select, { SingleValue } from "react-select";
import { useTranslation } from "react-i18next";
import { useGetCategoriesQuery } from "../../templates/ecommerce/product/api/productTaxonomyApi";
import Icon from "../../common/components/Icon/Icon";

interface Option {
  value: number;
  label: string;
  link: string;
}

const ProductFinder: React.FC = () => {
  const { t } = useTranslation();

  // 1. Fetch Categories Tree
  const { data: categoryTree = [], isLoading } = useGetCategoriesQuery();

  // 2. State
  const [selectedService, setSelectedService] = useState<Option | null>(null);
  const [selectedBrand, setSelectedBrand] = useState<Option | null>(null);
  const [selectedModel, setSelectedModel] = useState<Option | null>(null);

  // 3. Get Props
  const rootEl = document.getElementById("product-finder-root");
  const buttonText = rootEl?.dataset.buttonText || "SELECT";

  // 4. Derived Options
  const serviceOptions: Option[] = useMemo(() => {
    return categoryTree.map((cat) => ({
      value: cat.id,
      label: cat.name,
      link: cat.link,
    }));
  }, [categoryTree]);

  const brandOptions: Option[] = useMemo(() => {
    if (!selectedService) return [];
    const serviceNode = categoryTree.find(
      (c) => c.id === selectedService.value,
    );
    return (serviceNode?.children || []).map((cat) => ({
      value: cat.id,
      label: cat.name,
      link: cat.link,
    }));
  }, [categoryTree, selectedService]);

  const modelOptions: Option[] = useMemo(() => {
    if (!selectedService || !selectedBrand) return [];
    const serviceNode = categoryTree.find(
      (c) => c.id === selectedService.value,
    );
    const brandNode = serviceNode?.children.find(
      (c) => c.id === selectedBrand.value,
    );
    return (brandNode?.children || []).map((cat) => ({
      value: cat.id,
      label: cat.name,
      link: cat.link,
    }));
  }, [categoryTree, selectedService, selectedBrand]);

  // Logic for "All" Placeholder
  const isBrandDisabled = !selectedService || brandOptions.length === 0;
  const brandPlaceholder = !selectedService
    ? t("productFinder.selectBrand", "Select Brand")
    : brandOptions.length === 0
      ? t("productFinder.all", "All")
      : t("productFinder.selectBrand", "Select Brand");

  const isBrandAll = selectedService && brandOptions.length === 0;
  const isModelDisabled =
    (!selectedBrand && !isBrandAll) || modelOptions.length === 0;
  const modelPlaceholder =
    !selectedBrand && !isBrandAll
      ? t("productFinder.selectModel", "Select Model")
      : modelOptions.length === 0
        ? t("productFinder.all", "All")
        : t("productFinder.selectModel", "Select Model");

  // 5. Handlers
  const handleServiceChange = (opt: SingleValue<Option>) => {
    setSelectedService(opt);
    setSelectedBrand(null);
    setSelectedModel(null);
  };

  const handleBrandChange = (opt: SingleValue<Option>) => {
    setSelectedBrand(opt);
    setSelectedModel(null);
  };

  const handleModelChange = (opt: SingleValue<Option>) => {
    setSelectedModel(opt);
  };

  const handleSubmit = () => {
    if (selectedModel?.link) {
      window.location.href = selectedModel.link;
      return;
    }
    if (selectedBrand?.link) {
      window.location.href = selectedBrand.link;
      return;
    }
    if (selectedService?.link) {
      window.location.href = selectedService.link;
      return;
    }
  };

  // Custom Select Styles
  const selectStyles = {
    control: (base: any, state: any) => ({
      ...base,
      backgroundColor: "#2c2e33",
      borderColor: state.isFocused ? "var(--color-primary)" : "#3f4148",
      color: "#fff",
      minHeight: "50px",
      borderRadius: "4px",
      boxShadow: "none",
      opacity: state.isDisabled ? 0.7 : 1,
      "&:hover": {
        borderColor: state.isDisabled ? "#3f4148" : "var(--color-primary)",
      },
    }),
    menu: (base: any) => ({
      ...base,
      backgroundColor: "#2c2e33",
      border: "1px solid #3f4148",
      zIndex: 10,
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isFocused ? "#3f4148" : "transparent",
      color: "#fff",
      cursor: "pointer",
      "&:active": {
        backgroundColor: "var(--color-primary)",
      },
    }),
    singleValue: (base: any) => ({
      ...base,
      color: "#fff",
      fontWeight: 500,
    }),
    placeholder: (base: any) => ({
      ...base,
      color: "#9ca3af",
    }),
    dropdownIndicator: (base: any, state: any) => ({
      ...base,
      color: "#9ca3af",
      display: state.isDisabled ? "none" : "flex",
    }),
    indicatorSeparator: () => ({ display: "none" }),
  };

  if (isLoading) return null;

  return (
    <div className="product-finder-bar">
      {/* Step 1: Service */}
      <div className="product-finder-bar__step">
        <label className="product-finder-bar__label">
          {t("productFinder.serviceLabel", "Service")}
        </label>
        <div className="product-finder-bar__field">
          <span className="product-finder-bar__step-num">1</span>
          <div className="product-finder-bar__select-wrapper">
            <Select
              options={serviceOptions}
              value={selectedService}
              onChange={handleServiceChange}
              placeholder={t("productFinder.selectService", "Select Service")}
              styles={selectStyles}
              classNamePrefix="pf-select"
              isSearchable={false}
            />
          </div>
        </div>
      </div>

      {/* Step 2: Brand */}
      <div className="product-finder-bar__step">
        <label className="product-finder-bar__label">
          {t("productFinder.brandLabel", "Brand")}
        </label>
        <div className="product-finder-bar__field">
          <span className="product-finder-bar__step-num">2</span>
          <div className="product-finder-bar__select-wrapper">
            <Select
              options={brandOptions}
              value={selectedBrand}
              onChange={handleBrandChange}
              placeholder={brandPlaceholder}
              isDisabled={isBrandDisabled}
              styles={selectStyles}
              classNamePrefix="pf-select"
              isSearchable={true}
            />
          </div>
        </div>
      </div>

      {/* Step 3: Model */}
      <div className="product-finder-bar__step">
        <label className="product-finder-bar__label">
          {t("productFinder.modelLabel", "Model")}
        </label>
        <div className="product-finder-bar__field">
          <span className="product-finder-bar__step-num">3</span>
          <div className="product-finder-bar__select-wrapper">
            <Select
              options={modelOptions}
              value={selectedModel}
              onChange={handleModelChange}
              placeholder={modelPlaceholder}
              isDisabled={isModelDisabled}
              styles={selectStyles}
              classNamePrefix="pf-select"
              isSearchable={true}
            />
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="product-finder-bar__step product-finder-bar__step--submit">
        <button
          className="product-finder-bar__submit-btn"
          onClick={handleSubmit}
          disabled={!selectedService}
        >
          <Icon iconName="search" className="icon--stroke-thick" />
          <span>{buttonText}</span>
        </button>
      </div>
    </div>
  );
};

export default ProductFinder;
